import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { of, Observable } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom, tap } from 'rxjs/operators';
import { CommunicationsApiService } from './communications-api.service';
import {
  ConfiguredChannel,
  ChannelType,
  DistributionList,
  Template,
  ChannelUIds,
  MassNotificationTemplate,
  MassNotificationTemplateGroup,
  MassNotificationType,
  TimeOffsetType,
  MassNotification,
  CommunicationTemplate,
  DistributionListType
} from './communications.model';
import { GUID_EMPTY } from '@app/core/constants';
import { TranslateService } from '@ngx-translate/core';
import { v4 } from 'uuid';
import { FilterGroupType } from 'src/app/modules/core/store/models/filters.model';
import Environment from '@env';
import * as commActions from './communications.actions';
import * as commSelectors from './communications.selectors';
import * as fromRouting from 'src/app/modules/core/store/routing';
import { Logger } from 'src/app/modules/core/services/logger/logger.model';
import { LoggerService } from 'src/app/modules/core/services/logger/logger.service';
import { Location } from '@angular/common';
import produce from 'immer';
import { DatasetApiService } from 'src/app/modules/core/services/dataset-api.service';
import { QueryParams } from 'src/app/modules/core/store/models';
import { cloneDeep } from 'lodash-es';
import { MassNotificationWorkerService } from '../../web-workers/massnotification-worker.service';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';
import { getDefaultConfiguredChannelFromApi } from './communications.actions';

@Injectable()
export class CommunicationsEffects {
  private logger: Logger;

  constructor(
    private actions$: Actions,
    private commApiService: CommunicationsApiService,
    private store$: Store<any>,
    private notify: SystemNotificationsService,
    private translateService: TranslateService,
    private loggerService: LoggerService,
    private location: Location,
    private datasetApiService: DatasetApiService,
    private massNotificationWorkerService: MassNotificationWorkerService
  ) {
    this.logger = this.loggerService.getLogger('CommunicationEffects');
  }

  //
  // Channels
  //
  getOrganizationChannelsFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getOrganizationChannelsFromApi),
      withLatestFrom(this.store$.select(commSelectors.selectOrganizationChannelQueryParams)),
      switchMap(([action, queryParams]) => {
        return this.commApiService.getOrganizationChannels(queryParams).pipe(
          map(results => {
            return commActions.getOrganizationChannelsFromApiComplete({ data: results });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  getConfiguredChannelsFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getConfiguredChannelsFromApi),
      switchMap(action => {
        const { queryParams } = action;
        return this.commApiService.getConfiguredChannels(queryParams).pipe(
          map(results => {
            return commActions.getConfiguredChannelsFromApiComplete({ data: results });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  newTopoEmailChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newTopoEmailChannel),
      switchMap(action => {
        const { channel } = action;

        const newChannel: ConfiguredChannel = {
          uId: GUID_EMPTY,
          channelUId: channel.channelUId,
          name: channel.name,
          description: this.translateService.instant(channel.description),
          channelName: channel.name,
          channelDescription: channel.description,
          channelType: channel.channelType,
          channelTypeDisplay: this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channel.channelType}`),
          logoImagePath: channel.logoImagePath,
          isActive: true,
          settings: {},
          secureSettings: {},
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null
        };

        return [commActions.editTopoEmailChannel({ channel: newChannel })];
      })
    )
  );

  newSendGridEmailChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newSendGridEmailChannel),
      switchMap(action => {
        const { channel } = action;

        const newChannel: ConfiguredChannel = {
          uId: GUID_EMPTY,
          channelUId: channel.channelUId,
          name: channel.name,
          description: this.translateService.instant(channel.description),
          channelName: channel.name,
          channelDescription: channel.description,
          channelType: channel.channelType,
          channelTypeDisplay: this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channel.channelType}`),
          logoImagePath: channel.logoImagePath,
          isActive: true,
          settings: {
            fromEmail: '',
            apiToken: ''
          },
          secureSettings: {},
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null
        };

        return [commActions.editSendGridEmailChannel({ channel: newChannel })];
      })
    )
  );

  newEverbridgeChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newEverbridgeChannel),
      switchMap(action => {
        const { channel } = action;

        const newChannel: ConfiguredChannel = {
          uId: GUID_EMPTY,
          channelUId: channel.channelUId,
          name: channel.name,
          description: this.translateService.instant(channel.description),
          channelName: channel.name,
          channelDescription: channel.description,
          channelType: channel.channelType,
          channelTypeDisplay: this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channel.channelType}`),
          logoImagePath: channel.logoImagePath,
          isActive: true,
          settings: {
            organizationId: '',
            username: '',
            password: ''
          },
          secureSettings: {},
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null
        };

        return [commActions.editEverbridgeChannel({ channel: newChannel })];
      })
    )
  );

  newSlackChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newSlackChannel),
      switchMap(action => {
        const { channel } = action;

        const newChannel: ConfiguredChannel = {
          uId: GUID_EMPTY,
          channelUId: channel.channelUId,
          name: channel.name,
          description: this.translateService.instant(channel.description),
          channelName: channel.name,
          channelDescription: channel.description,
          channelType: channel.channelType,
          channelTypeDisplay: this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channel.channelType}`),
          logoImagePath: channel.logoImagePath,
          isActive: true,
          settings: {
            isConnected: false
          },
          secureSettings: {},
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null
        };

        return [commActions.editSlackChannel({ channel: newChannel })];
      })
    )
  );

  newTeamsChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newTeamsChannel),
      switchMap(action => {
        const { channel } = action;

        const newChannel: ConfiguredChannel = {
          uId: GUID_EMPTY,
          channelUId: channel.channelUId,
          name: channel.name,
          description: this.translateService.instant(channel.description),
          channelName: channel.name,
          channelDescription: channel.description,
          channelType: channel.channelType,
          channelTypeDisplay: this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channel.channelType}`),
          logoImagePath: channel.logoImagePath,
          isActive: true,
          settings: {
            isConnected: false
          },
          secureSettings: {},
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null
        };

        return [commActions.editMicrosoftTeamsChannel({ channel: newChannel })];
      })
    )
  );

  newTwilioChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newTwilioChannel),
      switchMap(action => {
        const { channel } = action;

        const newChannel: ConfiguredChannel = {
          uId: GUID_EMPTY,
          channelUId: channel.channelUId,
          name: channel.name,
          description: this.translateService.instant(channel.description),
          channelName: channel.name,
          channelDescription: channel.description,
          channelType: channel.channelType,
          channelTypeDisplay: this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channel.channelType}`),
          logoImagePath: channel.logoImagePath,
          isActive: true,
          settings: {},
          secureSettings: {},
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null
        };

        return [commActions.editTwilioChannel({ channel: newChannel })];
      })
    )
  );

  newTwilioTtsChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newTwilioTtsChannel),
      switchMap(action => {
        const { channel } = action;

        const newChannel: ConfiguredChannel = {
          uId: GUID_EMPTY,
          channelUId: channel.channelUId,
          name: channel.name,
          description: this.translateService.instant(channel.description),
          channelName: channel.name,
          channelDescription: channel.description,
          channelType: channel.channelType,
          channelTypeDisplay: this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channel.channelType}`),
          logoImagePath: channel.logoImagePath,
          isActive: true,
          settings: {
            phoneNumber: ''
          },
          secureSettings: {},
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null
        };

        return [commActions.editTwilioTtsChannel({ channel: newChannel })];
      })
    )
  );

  newWhatsAppChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newWhatsAppChannel),
      switchMap(action => {
        const { channel } = action;

        const newChannel: ConfiguredChannel = {
          uId: GUID_EMPTY,
          channelUId: channel.channelUId,
          name: channel.name,
          description: this.translateService.instant(channel.description),
          channelName: channel.name,
          channelDescription: channel.description,
          channelType: channel.channelType,
          channelTypeDisplay: this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channel.channelType}`),
          logoImagePath: channel.logoImagePath,
          isActive: true,
          settings: {},
          secureSettings: {},
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null
        };

        return [commActions.editWhatsAppChannel({ channel: newChannel })];
      })
    )
  );

  newHorizonSmsChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newHorizonSmsChannel),
      switchMap(action => {
        const { channel } = action;

        const newChannel: ConfiguredChannel = {
          uId: GUID_EMPTY,
          channelUId: channel.channelUId,
          name: channel.name,
          description: this.translateService.instant(channel.description),
          channelName: channel.name,
          channelDescription: channel.description,
          channelType: channel.channelType,
          channelTypeDisplay: this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channel.channelType}`),
          logoImagePath: channel.logoImagePath,
          isActive: true,
          settings: {},
          secureSettings: {},
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null
        };

        return [commActions.editHorizonSmsChannel({ channel: newChannel })];
      })
    )
  );

  newHorizonMobilPushSmsChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newHorizonMobilePushChannel),
      switchMap(action => {
        const { channel } = action;

        const newChannel: ConfiguredChannel = {
          uId: GUID_EMPTY,
          channelUId: channel.channelUId,
          name: channel.name,
          description: this.translateService.instant(channel.description),
          channelName: channel.name,
          channelDescription: channel.description,
          channelType: channel.channelType,
          channelTypeDisplay: this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channel.channelType}`),
          logoImagePath: channel.logoImagePath,
          isActive: true,
          settings: {},
          secureSettings: {},
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null
        };

        return [commActions.editHorizonMobilePushChannel({ channel: newChannel })];
      })
    )
  );

  newGwtsMobileChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newGwtsMobileChannel),
      switchMap(action => {
        const { channel } = action;

        const newChannel: ConfiguredChannel = {
          uId: GUID_EMPTY,
          channelUId: channel.channelUId,
          name: channel.name,
          description: this.translateService.instant(channel.description),
          channelName: channel.name,
          channelDescription: channel.description,
          channelType: channel.channelType,
          channelTypeDisplay: this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channel.channelType}`),
          logoImagePath: channel.logoImagePath,
          isActive: true,
          settings: {},
          secureSettings: {},
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null
        };

        return [commActions.editGwtsMobileChannel({ channel: newChannel })];
      })
    )
  );

  saveConfiguredChannelToApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.saveConfiguredChannelToApi),
      switchMap(action => {
        const { channel } = action;

        return this.commApiService.saveConfiguredChannel(channel).pipe(
          map(updatedChannel => {
            return commActions.saveConfiguredChannelToApiComplete({ channelUId: updatedChannel.channelUId });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  saveAndConnectToSlack$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.saveAndConnectToSlack),
      switchMap(action => {
        const { channel } = action;

        return this.commApiService.saveConfiguredChannel(channel).pipe(
          switchMap(updatedChannel => {
            return [
              commActions.updateSelectedConfiguredChannel({ channel: updatedChannel }),
              commActions.connectToSlack({ channel: updatedChannel })
            ];
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  connectToSlack$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(commActions.connectToSlack),
        switchMap(action => {
          const { channel } = action;
          const clientId = Environment.slack.client_id;
          const scopes = 'commands,chat:write,chat:write.public,channels:read,groups:read,users:read,users:read.email,im:write';
          const redirectUrl = encodeURIComponent(
            `${window.location.protocol}//${window.location.host}/api/v1/notifications/slack/auth/callback`
          );

          return this.commApiService.createChannelAuthStateToken(channel).pipe(
            map(stateToken => {
              const state = encodeURIComponent(stateToken);
              const url = `https://slack.com/oauth/v2/authorize?client_id=${clientId}&scope=${scopes}&redirect_uri=${redirectUrl}&state=${state}`;
              window.location.href = url;
            }),
            catchError(err => {
              return of(commActions.displayChannelError({ err }));
            })
          );
        })
      ),
    { dispatch: false }
  );

  getSlackChannelsForConfiguredChannelFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getSlackChannelsForConfiguredChannelFromApi),
      switchMap(action => {
        const { channel } = action;

        return this.commApiService.getSlackChannelsForConfiguredChannel(channel).pipe(
          map(channels => {
            return commActions.getSlackChannelsForConfiguredChannelFromApiComplete({ channels });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  disconnectFromSlackWithApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.disconnectFromSlackWithApi),
      switchMap(action => {
        const { channel } = action;

        return this.commApiService.disconnectChannelFromSlack(channel).pipe(
          map(updatedChannel => {
            return commActions.disconnectFromSlackWithApiComplete({ channel: updatedChannel });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  syncChannelMembersWithApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.syncChannelMembersWithApi),
      switchMap(action => {
        const { channelUId } = action;

        return this.commApiService.syncChannelMembers(channelUId).pipe(
          map(results => {
            const message = this.translateService.instant('MANAGEMENT.COMMUNICATION-CENTER.SYNC-STARTED');
            this.notify.success(message);
            return commActions.saveConfiguredChannelToApiComplete({ channelUId: channelUId });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  saveConfiguredChannelToApiComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.saveConfiguredChannelToApiComplete),
      withLatestFrom(this.store$.select(commSelectors.selectConfiguredChannelQueryParams)),
      switchMap(([action, queryParams]) => {
        return [commActions.closeConfiguredChannelEdit(), commActions.getConfiguredChannelsFromApi({ queryParams })];
      })
    )
  );

  closeConfiguredChannelEdit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.closeConfiguredChannelEdit),
      switchMap(action => {
        // Change URL without actually initiating a router navigation so that we preserve the list state
        this.location.go('/communications/channels');
        return [commActions.clearSelectedConfiguredChannel()];
      })
    )
  );

  deleteConfiguredChannelApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.deleteConfiguredChannelApi),
      switchMap(action => {
        const { channel } = action;

        return this.commApiService.deleteConfiguredChannel(channel).pipe(
          map(results => {
            return commActions.deleteConfiguredChannelApiComplete();
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  deleteConfiguredChannelApiComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.deleteConfiguredChannelApiComplete),
      withLatestFrom(this.store$.select(commSelectors.selectConfiguredChannelQueryParams)),
      switchMap(([action, queryParams]) => {
        return [commActions.clearChannelToDelete(), commActions.getConfiguredChannelsFromApi({ queryParams })];
      })
    )
  );

  //
  // Distribution Lists
  //
  getDistributionListsFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getDistributionListsFromApi),
      switchMap(action => {
        return this.commApiService.getDistributionLists(action.queryParams).pipe(
          map(results => {
            return commActions.getDistributionListsFromApiComplete({ data: results });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  newDistributionList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newDistributionList),
      switchMap(action => {
        const distributionList: DistributionList = {
          uId: GUID_EMPTY,
          name: '',
          description: '',
          channelType: ChannelType.Email,
          channelUId: null,
          nameFieldUId: null,
          destinationFieldUId: null,
          isActive: true,
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null,
          queryMatchRule: {
            uId: GUID_EMPTY,
            datasetUId: null,
            root: {
              uId: v4(),
              filters: [],
              filterGroups: [],
              groupType: FilterGroupType.And
            }
          },
          businessUnitUIds: null,
          distributionListType: DistributionListType.None
        };

        return [commActions.newDistributionListComplete({ distributionList })];
      })
    )
  );

  saveDistributionListToApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.saveDistributionListToApi),
      switchMap(action => {
        const { distributionList } = action;
        const isNew = distributionList.uId === GUID_EMPTY;

        return this.commApiService.saveDistributionList(distributionList).pipe(
          switchMap(distributionList => {
            if (isNew) {
              return [
                commActions.saveDistributionListToApiComplete({ distributionList }),
                fromRouting.go({
                  routeData: { path: ['communications', 'distribution-lists', distributionList.uId] }
                })
              ];
            } else {
              return [
                commActions.cancelEditDistributionListComplete(),
                fromRouting.go({
                  routeData: { path: ['communications', 'distribution-lists'] }
                })
              ];
            }
          }),
          catchError(err => of(commActions.displayDistributionListError({ err })))
        );
      })
    )
  );

  editDistributionList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.editDistributionList),
      switchMap(action => {
        const { distributionList } = action;

        return [
          commActions.editDistributionListComplete({ distributionList }),
          fromRouting.go({
            routeData: { path: ['communications', 'distribution-lists', distributionList.uId] }
          })
        ];
      })
    )
  );

  cancelEditDistributionList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.cancelEditDistributionList),
      switchMap(action => {
        return [
          commActions.cancelEditDistributionListComplete(),
          fromRouting.go({
            routeData: { path: ['communications', 'distribution-lists'] }
          })
        ];
      })
    )
  );

  deleteDistributionListApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.deleteDistributionListApi),
      switchMap(action => {
        const { distributionList } = action;

        return this.commApiService.deleteDistributionList(distributionList).pipe(
          map(results => {
            return commActions.deleteDistributionListApiComplete();
          }),
          catchError(err => of(commActions.displayDistributionListError({ err })))
        );
      })
    )
  );

  deleteDistributionListApiComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.deleteDistributionListApiComplete),
      withLatestFrom(this.store$.select(commSelectors.selectDistributionListsQueryParams)),
      switchMap(([action, queryParams]) => {
        return [commActions.clearDistributionListToDelete(), commActions.getDistributionListsFromApi({ queryParams })];
      })
    )
  );

  getDocumentsWithFilterFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getDocumentsWithFilterFromApi),
      switchMap(action => {
        const { datasetUId, queryParams, filter } = action;
        return this.commApiService.getDocumentsWithFilter(datasetUId, queryParams, filter).pipe(
          map(results => commActions.getDocumentsWithFilterFromApiComplete({ results })),
          catchError(err => of(commActions.displayDistributionListError({ err })))
        );
      })
    )
  );

  getDatasetFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getDatasetFromApi),
      switchMap(action => {
        const { datasetUId } = action;
        return this.commApiService.getDataset(datasetUId).pipe(
          map(dataset => commActions.getDatasetFromApiComplete({ dataset })),
          catchError(err => of(commActions.displayDistributionListError({ err })))
        );
      })
    )
  );

  //
  // Templates
  //
  getTemplatesFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getTemplatesFromApi),
      switchMap(action => {
        return this.commApiService.getTemplates(action.queryParams).pipe(
          map(results => {
            return commActions.getTemplatesFromApiComplete({ data: results });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  newTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newTemplate),
      switchMap(action => {
        const template: Template = {
          uId: GUID_EMPTY,
          name: '',
          description: '',
          channelType: ChannelType.Email,
          datasetUId: null,
          layout: null,
          isActive: true,
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null,
          settings: {
            toRecipients: [],
            ccRecipients: [],
            bccRecipients: [],
            toReadOnly: false,
            ccReadOnly: false,
            subject: null,
            sendAsGroup: false,
            sendDocumentAttachments: false
          }
        };

        return [commActions.newTemplateComplete({ template })];
      })
    )
  );

  saveTemplateToApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.saveTemplateToApi),
      withLatestFrom(this.store$.select(commSelectors.selectTemplateQueryParams)),
      switchMap(([action, queryParams]) => {
        const { template, skipNavigation } = action;
        const isNew = template.uId === GUID_EMPTY;

        return this.commApiService.saveTemplate(template).pipe(
          switchMap(updatedTemplate => {
            if (isNew) {
              return [
                commActions.saveTemplateToApiComplete({ template: updatedTemplate }),
                fromRouting.go({
                  routeData: { path: ['communications', 'templates', updatedTemplate.uId] }
                })
              ];
            } else {
              if (!skipNavigation) {
                return [
                  commActions.cancelEditTemplateComplete(),
                  fromRouting.go({
                    routeData: { path: ['communications', 'templates'] }
                  })
                ];
              } else {
                return [commActions.getTemplatesFromApi({ queryParams })];
              }
            }
          }),
          catchError(err => of(commActions.displayDistributionListError({ err })))
        );
      })
    )
  );

  copyTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.copyTemplate),
      withLatestFrom(this.store$.select(commSelectors.selectTemplateQueryParams)),
      switchMap(([action, queryParams]) => {
        const { template } = action;

        return this.commApiService.copyTemplate(template).pipe(
          switchMap(copiedTemplate => {
            return [
              commActions.saveTemplateToApiComplete({ template: copiedTemplate }),
              commActions.clearTemplateToCopy(),
              fromRouting.go({
                routeData: { path: ['communications', 'templates', copiedTemplate.uId] }
              })
            ];
          }),
          catchError(err => of(commActions.displayDistributionListError({ err })))
        );
      })
    )
  );

  editTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.editTemplate),
      switchMap(action => {
        const { template } = action;

        return [
          commActions.editTemplateComplete({ template }),
          fromRouting.go({
            routeData: { path: ['communications', 'templates', template.uId] }
          })
        ];
      })
    )
  );

  cancelEditTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.cancelEditTemplate),
      switchMap(action => {
        return [
          commActions.cancelEditDistributionListComplete(),
          fromRouting.go({
            routeData: { path: ['communications', 'templates'] }
          })
        ];
      })
    )
  );

  deleteTemplateApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.deleteTemplateApi),
      switchMap(action => {
        const { template } = action;

        return this.commApiService.deleteTemplate(template).pipe(
          map(results => {
            return commActions.deleteTemplateApiComplete();
          }),
          catchError(err => of(commActions.displayDistributionListError({ err })))
        );
      })
    )
  );

  deleteTemplateApiComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.deleteTemplateApiComplete),
      withLatestFrom(this.store$.select(commSelectors.selectTemplateQueryParams)),
      switchMap(([action, queryParams]) => {
        return [commActions.clearTemplateToDelete(), commActions.getTemplatesFromApi({ queryParams })];
      })
    )
  );

  getDefaultConfiguredChannel$ = createEffect(() =>
  this.actions$.pipe(
    ofType(commActions.getDefaultConfiguredChannelFromApi),
    switchMap(() =>
      this.commApiService.getDefaultConfiguredChannel().pipe(
        map(settings =>
          commActions.getDefaultConfiguredChannelComplete({ settings })
        )
      )
    )
  )
);

  // Everbridge Templates
  getEverbridgeTemplatesFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getEverbridgeTemplatesFromApi),
      switchMap(action => {
        return this.commApiService.getEverbridgeTemplatesForConfiguredChannel(action.channel).pipe(
          map(results => {
            return commActions.getEverbridgeTemplatesFromApiComplete({ templates: results });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  displayChannelError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.displayChannelError),
      switchMap(action => {
        this.logger.logError('Channel error: ' + action.err);
        if (action.err && action.err.length > 0) {
          const errorMessage = this.translateService.instant(action.err);
          this.notify.error(errorMessage);
        }
        return [];
      })
    )
  );

  displayDistributionListError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.displayDistributionListError),
      switchMap(action => {
        this.logger.logError('Distribution List error: ' + action.err);
        if (action.err && action.err.length > 0) {
          const errorMessage = this.translateService.instant(action.err);
          this.notify.error(errorMessage);
        }
        return [];
      })
    )
  );

  // Mass Notification Templates
  editMassNotificationTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.editMassNotificationTemplate),
      switchMap(action => {
        const { template } = action;

        return this.commApiService.getMassNotificationTemplate(template.uId).pipe(
          map(result => {
            return commActions.setSelectedMassNotificationTemplate({ template: result, isDirty: false });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  copyMassNotificationTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.copyMassNotificationTemplate),
      switchMap(action => {
        const { template } = action;

        return this.commApiService.getMassNotificationTemplate(template.uId).pipe(
          map(result => {
            var newTemplate = cloneDeep(result);
            newTemplate.uId = GUID_EMPTY;

            const copyText = this.translateService.instant('MANAGEMENT.COMMUNICATION-CENTER.COPY-OF');
            newTemplate.name = copyText + ' ' + template.name;

            // Change the uids on all the templates
            newTemplate.communicationTemplates?.forEach((commTemp: CommunicationTemplate) => {
              commTemp.uId = v4();

              commTemp.viewControls?.forEach(control => {
                control.uId = v4();
                control.communicationTemplateUId = commTemp.uId;
              });
            });
            return commActions.setSelectedMassNotificationTemplate({ template: newTemplate, isDirty: true });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  deleteMassNotificationTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.deleteMassNotificationTemplate),
      switchMap(action => {
        const { template } = action;

        return this.commApiService.deleteMassNotificationTemplate(template.uId).pipe(
          switchMap(results => {
            return [
              commActions.setSelectedMassNotificationTemplate({ template: null, isDirty: false }),
              commActions.setMassNotificationTemplatesTableChanged({ changed: true })
            ];
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  newMassNotificationTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newMassNotificationTemplate),
      map(action => {
        const template: MassNotificationTemplate = {
          uId: GUID_EMPTY,
          name: this.translateService.instant('COMMUNICATIONS.NOTIFICATION-TEMPLATE'),
          description: '',
          isActive: false,
          type: MassNotificationType.Announcement,
          massNotificationWorkflowUId: null,
          massNotificationRecipientsWorkflowUId: null,
          communicationTemplates: [],
          businessUnitUIds: null,
          channelsCascadeSettings: {
            advancedMode: false
            /*            expirationTime: {
              offsetType: TimeOffsetType.Hours,
              offsetValue: 1
            }*/
          }
        };

        return commActions.setSelectedMassNotificationTemplate({ template, isDirty: true });
      })
    )
  );

  saveSelectedMassNotificationTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.saveSelectedMassNotificationTemplate),
      switchMap(({ template, close }) => {
        if (template.uId === GUID_EMPTY) {
          return [commActions.createMassNotificationTemplate({ template, close })];
        } else {
          return [commActions.updateMassNotificationTemplate({ template, close })];
        }
      })
    )
  );

  createMassNotificationTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.createMassNotificationTemplate),
      switchMap(action => {
        return this.commApiService.createMassNotificationTemplate(action.template).pipe(
          switchMap(template => {
            return [
              commActions.setSelectedMassNotificationTemplate({ template: action.close ? null : template, isDirty: false }),
              commActions.setMassNotificationTemplatesTableChanged({ changed: true })
            ];
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  updateMassNotificationTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.updateMassNotificationTemplate),
      switchMap(action => {
        return this.commApiService.updateMassNotificationTemplate(action.template).pipe(
          switchMap(template => {
            return [
              commActions.setSelectedMassNotificationTemplate({ template: action.close ? null : template, isDirty: false }),
              commActions.setMassNotificationTemplatesTableChanged({ changed: true })
            ];
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  setMassNotificationTemplateActive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.setMassNotificationTemplateActive),
      switchMap(action => {
        return this.commApiService.getMassNotificationTemplate(action.template.uId).pipe(
          map(result => {
            if (!result) {
              return;
            }
            const updatedTemplate = produce(result, draftState => {
              draftState.isActive = action.active;
            });

            return commActions.updateMassNotificationTemplate({ template: updatedTemplate, close: true });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  // Mass Notification Template Groups
  editMassNotificationTemplateGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.editMassNotificationTemplateGroup),
      switchMap(action => {
        const { templateGroup } = action;

        return this.commApiService.getMassNotificationTemplateGroup(templateGroup.uId).pipe(
          map(result => {
            return commActions.setSelectedMassNotificationTemplateGroup({ templateGroup: result, isDirty: false });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  copyMassNotificationTemplateGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.copyMassNotificationTemplateGroup),
      switchMap(action => {
        const { templateGroup } = action;

        return this.commApiService.getMassNotificationTemplateGroup(templateGroup.uId).pipe(
          map(result => {
            var newTemplateGroup = cloneDeep(result);
            newTemplateGroup.uId = GUID_EMPTY;

            const copyText = this.translateService.instant('MANAGEMENT.COMMUNICATION-CENTER.COPY-OF');
            newTemplateGroup.name = copyText + ' ' + templateGroup.name;

            return commActions.setSelectedMassNotificationTemplateGroup({ templateGroup: newTemplateGroup, isDirty: true });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  deleteMassNotificationTemplateGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.deleteMassNotificationTemplateGroup),
      switchMap(action => {
        const { templateGroup } = action;

        return this.commApiService.deleteMassNotificationTemplateGroup(templateGroup.uId).pipe(
          switchMap(results => {
            return [
              commActions.setSelectedMassNotificationTemplateGroup({ templateGroup: null, isDirty: false }),
              commActions.setMassNotificationTemplateGroupsTableChanged({ changed: true })
            ];
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  newMassNotificationTemplateGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newMassNotificationTemplateGroup),
      map(action => {
          const templateGroup: MassNotificationTemplateGroup = {
          uId: GUID_EMPTY,
          name: this.translateService.instant('COMMUNICATIONS.NOTIFICATION-TEMPLATE-GROUP')
        };

        return commActions.setSelectedMassNotificationTemplateGroup({ templateGroup, isDirty: true });
      })
    )
  );

  saveSelectedMassNotificationTemplateGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.saveSelectedMassNotificationTemplateGroup),
      withLatestFrom(this.store$.select(commSelectors.selectSelectedMassNotificationTemplateGroup)),
      switchMap(([action, templateGroup]) => {
        if (templateGroup.uId === GUID_EMPTY) {
          return [commActions.createMassNotificationTemplateGroup({ templateGroup, close: action.close })];
        } else {
          return [commActions.updateMassNotificationTemplateGroup({ templateGroup, close: action.close })];
        }
      })
    )
  );

  createMassNotificationTemplateGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.createMassNotificationTemplateGroup),
      switchMap(action => {
        return this.commApiService.createMassNotificationTemplateGroup(action.templateGroup).pipe(
          switchMap(templateGroup => {
            return [
              commActions.setSelectedMassNotificationTemplateGroup({ templateGroup: action.close ? null : templateGroup, isDirty: false }),
              commActions.setMassNotificationTemplateGroupsTableChanged({ changed: true })
            ];
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  updateMassNotificationTemplateGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.updateMassNotificationTemplateGroup),
      switchMap(action => {
        return this.commApiService.updateMassNotificationTemplateGroup(action.templateGroup).pipe(
          switchMap(templateGroup => {
            return [
              commActions.setSelectedMassNotificationTemplateGroup({ templateGroup: action.close ? null : templateGroup, isDirty: false }),
              commActions.setMassNotificationTemplateGroupsTableChanged({ changed: true })
            ];
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  hasMassNotificationTemplateGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.hasMassNotificationTemplateGroups),
      switchMap(action => {
        return this.commApiService.hasMassNotificationTemplateGroups().pipe(
          switchMap(results => {
            return [
              commActions.setMassNotificationTemplateGroupsTableChanged({ changed: true })
            ];
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  // Mass Notification
  newMassNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newMassNotification),
      switchMap(action => {
        const notification = {
          uId: GUID_EMPTY
        } as MassNotification;

        return [
          commActions.setMassNotificationIsEditing({ isEditing: true }),
          commActions.setSelectedMassNotification({ notification, isDirty: true })
        ];
      })
    )
  );

  reviewMassNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.reviewMassNotification),
      switchMap(action => {
        const { notification } = action;

        return this.commApiService.getMassNotification(notification.uId).pipe(
          switchMap(result => {
            return [
              commActions.setMassNotificationIsEditing({ isEditing: false }),
              commActions.setSelectedMassNotification({ notification: result, isDirty: false })
            ];
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  editMassNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.editMassNotification),
      switchMap(action => {
        const { notification } = action;

        return this.commApiService.getMassNotification(notification.uId).pipe(
          switchMap(result => {
            return [
              commActions.setMassNotificationIsEditing({ isEditing: true }),
              commActions.setSelectedMassNotification({ notification: result, isDirty: false })
            ];
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  deleteMassNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.deleteMassNotification),
      switchMap(action => {
        const { notification } = action;

        return this.commApiService.deleteMassNotification(notification.uId).pipe(
          switchMap(results => {
            return [
              commActions.setSelectedMassNotification({ notification: null, isDirty: false }),
              commActions.setMassNotificationsTableChanged({ changed: true })
            ];
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  saveSelectedMassNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.saveSelectedMassNotification),
      withLatestFrom(this.store$.select(commSelectors.selectSelectedMassNotification)),
      switchMap(([action, notification]) => {
        if (notification.uId === GUID_EMPTY) {
          return [
            commActions.createMassNotification({
              notification,
              close: action.close,
              monitor: action.monitor,
              successMessage: action.successMessage
            })
          ];
        } else {
          return [commActions.updateMassNotification({ notification, close: action.close, successMessage: action.successMessage })];
        }
      })
    )
  );

  createMassNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.createMassNotification),
      switchMap(action => {
        return this.commApiService.createMassNotification(action.notification).pipe(
          switchMap(notification => {
            if (!!action.successMessage) {
              this.notify.success(action.successMessage);
            }
            if (action.monitor === true) {
              this.massNotificationWorkerService.unsubscribeToMassNotificationUpdate(null);
              this.massNotificationWorkerService.subscribeToMassNotificationUpdate(notification.uId);
            }
            return [
              commActions.setSelectedMassNotification({ notification: action.close ? null : notification, isDirty: false }),
              commActions.setMassNotificationsTableChanged({ changed: true })
            ];
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  updateMassNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.updateMassNotification),
      switchMap(action => {
        return this.commApiService.updateMassNotification(action.notification).pipe(
          switchMap(notification => {
            if (!!action.successMessage) {
              this.notify.success(action.successMessage);
            }
            return [
              commActions.setSelectedMassNotification({ notification: action.close ? null : notification, isDirty: false }),
              commActions.setMassNotificationsTableChanged({ changed: true })
            ];
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  updateMassNotificationRecipient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.updateMassNotificationRecipient),
      switchMap(action => {
        return this.commApiService.updateMassNotificationRecipient(action.recipient).pipe(
          switchMap(recipient => {
            return [
              commActions.setSelectedMassNotificationRecipient({ recipient: action.close ? null : recipient, isDirty: false }),
              commActions.setMassNotificationRecipientsTableChanged({ changed: true })
            ];
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  transitionRecipientsToWorkflowStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.transitionRecipientsToWorkflowStep),
      switchMap(action => {
        const request = {
          recipientUIds: action.recipientUIds,
          targetWorkflowStepUId: action.targetStepUId
        };
        return this.commApiService.transitionRecipientsWorkflow(request).pipe(
          switchMap(recipient => {
            return [commActions.setMassNotificationRecipientsTableChanged({ changed: true })];
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  setError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(commActions.setError),
        switchMap(action => {
          const message = action.err?.message;

          if (Boolean(message)) {
            const errorMessage = this.translateService.instant(`ERRORS.${message}`);
            this.notify.error(errorMessage);
            return [];
          } else {
            console.log(action.err);
            return [];
          }
        })
      ),
    {
      dispatch: false
    }
  );

  loadDatasetsForSelect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.loadDatasetsForSelect),
      switchMap(action => {
        const queryParams: QueryParams = {
          skip: 0,
          take: 500,
          sortField: 'Name',
          sortDirection: 'Ascending'
        };

        return this.datasetApiService.getDatasets(queryParams, true, true).pipe(
          map(res => commActions.loadDatasetsForSelectComplete({ results: res })),
          catchError(err => of(commActions.setError({ err })))
        );
      })
    )
  );

  getWhatsAppTemplatesFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getWhatsAppTemplatesFromApi),
      switchMap(action => {
        return this.commApiService.getWhatsAppTemplateDetails(action.cultureCode).pipe(
          map(res => commActions.getWhatsAppTemplatesFromApiComplete({ results: res })),
          catchError(err => of(commActions.setError({ err })))
        );
      })
    )
  );

  getWhatsAppTemplateGroupFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getWhatsAppTemplateGroupFromApi),
      switchMap(action => {
        return this.commApiService.getWhatsAppTemplateGroup(action.templateGroupUId).pipe(
          map(res => commActions.getWhatsAppTemplateGroupFromApiComplete({ results: res })),
          catchError(err => of(commActions.setError({ err })))
        );
      })
    )
  );
}
